import React from 'react';
import {EntityRequest, IGlobalSearchContext} from './sections';

/** macOS gets its own combo: Option mangles character input, so Cmd+Shift+F instead. */
export const IS_MAC = typeof navigator !== 'undefined'
    && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || '');
export const HOTKEY_LABEL = IS_MAC ? '\u2318 \u21e7 F' : 'Ctrl Alt F';
export const HOTKEY_TITLE = IS_MAC ? 'Search (\u2318+Shift+F)' : 'Search (Ctrl+Alt+F)';

/**
 * Ctrl+Alt+F (Cmd+Shift+F on macOS) opens the global search palette from anywhere, including
 * while typing in a text field. Deliberately not Ctrl+K/Ctrl+F, which Chrome owns.
 * Escape-to-close is the Modal's own dismiss handling, not this hook's concern.
 */
export const useGlobalSearchHotkey = (open: () => void): void => {
    const openRef = React.useRef(open);
    openRef.current = open;

    React.useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const combo = IS_MAC
                ? e.metaKey && e.shiftKey && e.key.toLowerCase() === 'f'
                : e.ctrlKey && e.altKey && e.key.toLowerCase() === 'f';
            if (combo && !e.isComposing) {
                e.preventDefault();
                openRef.current();
            }
        };
        // Capture phase: Fluent's FocusZone (the header CommandBar) stops propagation of some
        // keydowns, which swallowed the hotkey whenever a nav item held focus.
        document.addEventListener('keydown', handler, true);
        return () => document.removeEventListener('keydown', handler, true);
    }, []);
};

/** How a programmatic open wants the palette to start. */
export interface IGlobalSearchOpenOptions {
    /** 'products' opens straight into the ">" product search mode. */
    mode?: 'products';
    /** Overrides the route-derived context, e.g. a bundle editor that is a panel, not a page. */
    context?: IGlobalSearchContext;
}

const OPEN_EVENT = 'ac:global-search-open';

/**
 * Open the palette from anywhere (e.g. an empty workspace's "Add something?" button) without
 * threading callbacks up to the header: NavMenu listens for this event next to the hotkey.
 */
export const openGlobalSearch = (options: IGlobalSearchOpenOptions = {}): void => {
    window.dispatchEvent(new CustomEvent<IGlobalSearchOpenOptions>(OPEN_EVENT, { detail: options }));
};

/** The listening half of openGlobalSearch, mounted once by NavMenu. */
export const useGlobalSearchOpenRequests = (open: (options: IGlobalSearchOpenOptions) => void): void => {
    const openRef = React.useRef(open);
    openRef.current = open;
    React.useEffect(() => {
        const handler = (e: Event) => openRef.current((e as CustomEvent<IGlobalSearchOpenOptions>).detail ?? {});
        window.addEventListener(OPEN_EVENT, handler);
        return () => window.removeEventListener(OPEN_EVENT, handler);
    }, []);
};

const OPEN_ENTITY_EVENT = 'ac:open-entity';

/**
 * Ask the header's CreateEntityHost to show an entity (a product's details, a contact, …) from
 * anywhere, e.g. a grid thumbnail opening the product on its images tab. Same bus pattern as
 * openGlobalSearch; NavMenu listens.
 */
export const openEntity = (request: EntityRequest): void => {
    window.dispatchEvent(new CustomEvent<EntityRequest>(OPEN_ENTITY_EVENT, { detail: request }));
};

/** The listening half of openEntity, mounted once by NavMenu. */
export const useEntityOpenRequests = (open: (request: EntityRequest) => void): void => {
    const openRef = React.useRef(open);
    openRef.current = open;
    React.useEffect(() => {
        const handler = (e: Event) => {
            const request = (e as CustomEvent<EntityRequest>).detail;
            if (request) openRef.current(request);
        };
        window.addEventListener(OPEN_ENTITY_EVENT, handler);
        return () => window.removeEventListener(OPEN_ENTITY_EVENT, handler);
    }, []);
};
